import {
    GetSponsorPlansRequest,
    type GetSponsorPlansRequest_SortDirection,
    type GetSponsorPlansRequest_SortType,
    type SponsorPlanItem,
} from '@app/_proto/Protos/sponsors';
import { SponsorsClient } from '@app/_proto/Protos/sponsors.client';
import { sortSponsorPlans } from '@app/domains/champion/utils/sortSponsorPlans';
import { reducePartialRequest } from '@app/domains/common/utils/grpc';
import { useGrpcApiWithQuery } from '@app/libs/api';
import { useAuth } from '@app/libs/auth';
import { useGrpcRequest } from '@app/libs/grpc';
import type { PartialMessage } from '@protobuf-ts/runtime';
import { useMemo } from 'react';

type UseSortedAndAuthChampionPlansParam = {
    novelId: number | undefined;
    sortType: GetSponsorPlansRequest_SortType | undefined;
    sortDirection: GetSponsorPlansRequest_SortDirection | undefined;
    enabled?: boolean;
    suspense?: boolean;
    showModalOnError?: boolean;
    requestOption?: PartialMessage<GetSponsorPlansRequest>;
};

type UseSortedAndAuthChampionPlansReturn = {
    data?: SponsorPlanItem[];
    isLoading: boolean;
};

export default function useSortedAndAuthChampionPlans({
    novelId,
    sortType,
    sortDirection,
    enabled,
    suspense,
    showModalOnError,
}: UseSortedAndAuthChampionPlansParam): UseSortedAndAuthChampionPlansReturn {
    const { user } = useAuth();
    const getRequestQueryParams = reducePartialRequest({
        novelId,
        sortType,
        sortDirection,
    });
    const getChampionPlansRequest = useGrpcRequest(GetSponsorPlansRequest, getRequestQueryParams);
    const { data, isLoading } = useGrpcApiWithQuery(
        SponsorsClient,
        c => c.getSponsorPlans,
        getChampionPlansRequest,
        ['sponsorPlans', user?.id, novelId, sortType, sortDirection],
        {
            enabled: (!!user && enabled) ?? true,
            suspense: suspense,
            showModalOnError: showModalOnError ?? true,
            keepPreviousData: false,
        }
    );

    const sortedSponsorPlans = useMemo(() => {
        if (!user) {
            return undefined;
        }
        return sortSponsorPlans(data?.items);
    }, [user, data?.items]);

    return {
        data: sortedSponsorPlans,
        isLoading,
    };
}
